
import { useState, useEffect } from "react";
import { FileVideo, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { CustomerInput } from "@/shared/components/ui/customer-input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Switch } from "@/shared/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useToast } from "@/shared/hooks/use-toast";
import { useSettings } from "@/shared/contexts/SettingsContext";
import { useSubscriptionGuard } from "@/shared/hooks/useSubscriptionGuard";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";
import { apiClient } from "@/shared/services/apiClient";
import { API_ROUTES } from "@/shared/services/apiRoutes";
import { supabase } from "@/integrations/supabase/client";
import { useTeamMembers } from "@/shared/hooks/useTeamMembers";
import { useCustomers } from "@/shared/hooks/useCustomers";
import { useAuth } from "@/shared/contexts/AuthContext";
import { apiClient as apiClientV2 } from "@/shared/services/api";
import { getShareableBaseUrl } from "@/utils/getShareableBaseUrl";

interface RequestRecordingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Filled by Chrome extension when customer data was captured on Housecall Pro */
  prefillData?: {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    email?: string;
  };
}

export function RequestRecordingModal({ open, onOpenChange, prefillData }: RequestRecordingModalProps) {
  const { requestRecordingMessage, replaceTokens, organizationName } = useSettings();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { checkAccess } = useSubscriptionGuard();
  const { usage } = useSubscription();
  const { createCustomer, customers, refetch: refetchCustomers } = useCustomers();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    countryCode: "+1",
    email: "",
    message: "",
    shareOnEmail: false,
    shareRecording: "user",
    assignTo: ""
  });
  const { teamMembers, loading: loadingTeam } = useTeamMembers();
  const [isLoading, setIsLoading] = useState(false);
  const [userRole, setUserRole] = useState<'admin' | 'user' | null>(null);

  useEffect(() => {
    if (prefillData && open) {
      setFormData((prev) => {
        let countryCode = prev.countryCode;
        let phoneNumber = prev.phoneNumber;
        if (prefillData.phoneNumber) {
          const digits = prefillData.phoneNumber.replace(/\D/g, "");
          if (digits.length >= 10) {
            countryCode = "+1";
            phoneNumber = digits.slice(-10);
          }
        }
        const emailTrim = (prefillData.email || "").trim();
        return {
          ...prev,
          firstName: prefillData.firstName || prev.firstName,
          lastName: prefillData.lastName || prev.lastName,
          countryCode,
          phoneNumber,
          email: emailTrim || prev.email,
          shareOnEmail: emailTrim ? true : prev.shareOnEmail,
        };
      });
    }
  }, [prefillData, open]);

  // Debug: Log team members and subscription status
  useEffect(() => {
    console.log('[RequestRecordingModal] teamMembers:', teamMembers.length, teamMembers);
    console.log('[RequestRecordingModal] loadingTeam:', loadingTeam);
    console.log('[RequestRecordingModal] usage:', {
      subscribed: usage?.subscribed,
      planType: usage?.planType,
      isTrialActive: usage?.isTrialActive,
      is_pro_trial: usage?.is_pro_trial
    });
    const showDropdown = usage?.subscribed && usage?.planType !== 'free' && (!usage?.isTrialActive || usage?.is_pro_trial);
    console.log('[RequestRecordingModal] Should show dropdown:', showDropdown);
  }, [teamMembers, loadingTeam, usage]);

  // Live-call style SMS flow for recording requests: compose full URL client-side
  const handleSubmitDirect = async (e: React.FormEvent) => {
    e.preventDefault();

    // SUBSCRIPTION GUARD: Check subscription access before proceeding
    if (!checkAccess('Request Recording')) {
      return; // checkAccess handles toast and navigation
    }

    if (!formData.firstName.trim() || !formData.phoneNumber.trim()) {
      toast({
        title: "Error",
        description: "Please fill in first name and phone number",
        variant: "destructive",
      });
      return;
    }

    // Validate phone number - only allow digits and optional + at start
    const phoneRegex = /^\+?\d+$/;
    if (!phoneRegex.test(formData.phoneNumber.trim())) {
      toast({
        title: "Invalid Phone Number",
        description: "Phone number can only contain digits and an optional + at the beginning",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('You must be logged in');

      // Save/update customer using useCustomers hook
      const fullPhoneNumber = `${formData.countryCode}${formData.phoneNumber}`;
      const customerName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();

      let endCustomer = null;
      try {
        endCustomer = await createCustomer({
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          phone_number: fullPhoneNumber,
          email: formData.shareOnEmail ? formData.email : null,
          project: null,
          assigned_to: formData.assignTo || null,
        });
      } catch (error: any) {
        // If customer already exists with this phone number, find them
        if (error.message?.includes('already have a customer with phone number')) {
          console.log('[RequestRecordingModal] Customer already exists, searching for them');

          // First try to find in current customers list
          let existingCustomers = customers.filter(c => c.phone_number === fullPhoneNumber);

          // If not found, refetch customers and try again
          if (existingCustomers.length === 0) {
            console.log('[RequestRecordingModal] Customer not in cache, refetching...');
            await refetchCustomers();
            existingCustomers = customers.filter(c => c.phone_number === fullPhoneNumber);
          }

          if (existingCustomers.length > 0) {
            endCustomer = existingCustomers[0];
            console.log('[RequestRecordingModal] Found existing customer:', endCustomer.id);

            // Show informative message to user
            toast({
              title: "Using Existing Customer",
              description: `Found existing customer "${endCustomer.name}" with this phone number.`,
              variant: "default"
            });
          } else {
            // Customer exists but not visible to this user - show error and stop
            toast({
              title: "Duplicate Phone Number",
              description: `A customer with phone number ${fullPhoneNumber} already exists in your contacts. Please use a different phone number or delete the duplicate customer first.`,
              variant: "destructive"
            });
            return;
          }
        } else {
          throw error;
        }
      }

      const endCustomerId = endCustomer?.id || null;
      console.log('End customer ID for recording request:', endCustomerId);

      // Get company_id for recording_requests table
      let companyId: string;
      try {
        const companyData = await apiClient.get<{ ok: boolean; company_id: string }>('/user/company-id');
        console.log('[RequestRecordingModal] Company data response:', companyData);
        companyId = companyData?.company_id;
        if (!companyId) {
          console.error('[RequestRecordingModal] No company_id in response:', companyData);
          throw new Error('Company not found. Please complete your profile setup.');
        }
      } catch (error: any) {
        console.error('[RequestRecordingModal] Failed to get company_id:', error);
        throw new Error(error.message || 'Failed to get company information');
      }

      // Build full link like /video-call (no backend token)
      const recordingRoomId = `room_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      const recordLink = `${getShareableBaseUrl()}/record/${recordingRoomId}?direct=1`;
      const messageText = `${getFinalMessage()}\n\nRecord your video here: ${recordLink}`;

      // ✅ NEW: Create recording request via backend API
      let expirationDate: Date | null = null;
      try {
        const recordingResponse = await apiClient.post<{ ok: boolean; expiresAt?: string }>(API_ROUTES.RECORDING_REQUEST, {
          roomId: recordingRoomId,
          phone: fullPhoneNumber,
          customerPhone: fullPhoneNumber, // Backwards compatibility
          customerName: customerName,
          email: formData.shareOnEmail ? formData.email : null,
          assignedTo: formData.assignTo || user.id,
        });

        // Get expiration date from backend response (48 hours from now)
        if (recordingResponse.expiresAt) {
          expirationDate = new Date(recordingResponse.expiresAt);
        }

        console.log('✅ Recording request created via backend API, expires:', expirationDate);
      } catch (requestError) {
        console.error('Failed to create recording request:', requestError);
        // Fallback: Set expiration to 48 hours from now
        expirationDate = new Date(Date.now() + 48 * 60 * 60 * 1000);
      }

      // Build SMS message with actual expiration date/time
      const messageWithExpiration = expirationDate
        ? `${getFinalMessage().replace('{time}', format(expirationDate, 'h:mm a')).replace('{date}', format(expirationDate, 'MMMM do'))}\n\nRecord your video here: ${recordLink}`
        : messageText; // Fallback to original message if no expiration

      // SECURITY: Use backend proxy instead of calling edge function directly
      const smsData = await apiClientV2.post<{ success: boolean; error?: string }>('/edge/send-sms', {
        to: fullPhoneNumber,
        message: messageWithExpiration,
        type: 'recording-request',
      });

      if (!smsData.success) {
        console.error('send-sms failed:', smsData);
        throw new Error(smsData.error ?? 'Failed to send SMS');
      }

      // Send email notification if email is provided
      if (formData.shareOnEmail && formData.email?.trim()) {
        try {
          // SECURITY: Use backend API instead of direct database query
          const userProfile = await apiClient.get<{ ok: boolean; profile: { full_name?: string } }>('/user/profile');
          const hostName = userProfile?.profile?.full_name || 'Your host';

          // SECURITY: Use backend proxy instead of calling edge function directly
          await apiClientV2.post('/edge/send-email', {
            to: formData.email.trim(),
            subject: `${hostName} has requested a video recording from you`,
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2>Video Recording Request</h2>
                <p>Hello ${customerName},</p>
                <p><strong>${hostName}</strong> has requested a video recording from you.</p>
                <p style="margin-top: 20px;">
                  <a href="${recordLink}" style="background-color: #94ACF8; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">Record Your Video</a>
                </p>
                <p style="color: #666; font-size: 12px; margin-top: 30px;">This request was sent by ${hostName} via Wlkthru.</p>
              </div>
            `,
            from: 'noreply@wlkthru.co',
            fromName: 'Wlkthru',
          });
          console.log(`✅ Email invitation sent to ${formData.email.trim()}`);
        } catch (emailError) {
          console.error('⚠️ Failed to send email invitation:', emailError);
          // Don't fail the request if email fails
        }
      }

      toast({
        title: 'Recording Request Sent!',
        description: `Recording request sent to ${customerName} at ${fullPhoneNumber}${formData.shareOnEmail && formData.email ? ' (SMS & Email)' : ' (SMS)'}`,
      });
      onOpenChange(false);
    } catch (err) {
      console.error('Error sending SMS:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : (typeof err === 'string' ? err : 'Failed to send SMS invitation'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Generate message preview with tokens replaced
  const getMessagePreview = () => {
    const message = formData.message || requestRecordingMessage;

    // Get assigned user's name from team members
    const assignedUser = teamMembers.find(tm => tm.id === formData.assignTo);
    const currentUserName = assignedUser?.name || '';

    const customerFullName = `${formData.firstName} ${formData.lastName}`.trim();

    return replaceTokens(message, {
      name: customerFullName || undefined, // Keep placeholder visible if no name
      firstName: formData.firstName.trim() || undefined,
      lastName: formData.lastName.trim() || undefined,
      organization: organizationName,
      date: format(new Date(), "MMMM, do"),
      time: format(new Date(), "h:mm a"),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      userName: currentUserName || undefined
    });
  };

  // Generate final message for sending (replaces tokens with values or empty string)
  const getFinalMessage = () => {
    const message = formData.message || requestRecordingMessage;

    // Get assigned user's name from team members
    const assignedUser = teamMembers.find(tm => tm.id === formData.assignTo);
    const currentUserName = assignedUser?.name || '';

    const customerFullName = `${formData.firstName} ${formData.lastName}`.trim();

    return replaceTokens(message, {
      name: customerFullName || '', // Replace with actual value or empty string
      firstName: formData.firstName.trim() || '',
      lastName: formData.lastName.trim() || '',
      organization: organizationName,
      date: format(new Date(), "MMMM, do"),
      time: format(new Date(), "h:mm a"),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      userName: currentUserName || ''
    });
  };


  // Fetch user role from team members
  useEffect(() => {
    if (user?.id && teamMembers.length > 0) {
      const currentMember = teamMembers.find(m => m.id === user.id);
      if (currentMember) {
        setUserRole(currentMember.role as 'admin' | 'user');
      }
    }
  }, [user, teamMembers]);

  // Set default assignTo to current user when teamMembers loads
  useEffect(() => {
    if (teamMembers.length > 0 && !formData.assignTo) {
      supabase.auth.getUser().then(({ data }) => {
        if (data.user) {
          setFormData(prev => ({ ...prev, assignTo: data.user.id }));
        }
      });
    }
  }, [teamMembers, formData.assignTo]);

  const handleBack = () => {
    onOpenChange(false);
  };

  // Clear form when modal is closed
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setFormData({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        countryCode: "+1",
        email: "",
        message: "",
        shareOnEmail: false,
        shareRecording: "user",
        assignTo: ""
      });
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[400px] max-h-[80vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-accent/10 to-accent/5">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <div className="p-2 bg-accent/10 rounded-lg">
              <FileVideo className="h-5 w-5 text-accent" />
            </div>
            Request a Recording
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmitDirect} className="px-4 py-3 space-y-3">
          {/* First Name with autocomplete */}
          <div className="space-y-1">
            <Label className="text-xs font-medium">First Name *</Label>
            <CustomerInput
              placeholder="Enter first name"
              value={formData.firstName}
              onChange={(value) => setFormData((prev) => ({ ...prev, firstName: value }))}
              onCustomerSelect={(customer) => {
                // Extract phone number: always use +1 and take last 10 digits
                const digitsOnly = customer.phone_number?.replace(/\D/g, '') || "";
                const last10Digits = digitsOnly.slice(-10);

                setFormData((prev) => ({
                  ...prev,
                  firstName: customer.first_name || "",
                  lastName: customer.last_name || "",
                  phoneNumber: last10Digits,
                  countryCode: "+1",
                  email: customer.email || "",
                  shareOnEmail: !!customer.email, // Auto-enable if email exists
                }));
              }}
              className="text-xs"
              required
              field="name"
            />
          </div>

          {/* Last Name */}
          <div className="space-y-1">
            <Label className="text-xs font-medium">Last Name</Label>
            <Input
              placeholder="Enter last name (optional)"
              value={formData.lastName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, lastName: e.target.value }))
              }
              className="text-xs"
            />
          </div>

          {/* Phone Number */}
          <div className="space-y-1">
            <Label htmlFor="phone" className="text-xs font-medium">
              Phone Number
            </Label>
            <div className="flex gap-2">
              <div className="w-24">
                <Select value={formData.countryCode} onValueChange={(code) => setFormData(prev => ({ ...prev, countryCode: code }))}>
                  <SelectTrigger className="text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background border shadow-md z-50">
                    <SelectItem value="+1">🇺🇸 +1</SelectItem>
                    <SelectItem value="+44">🇬🇧 +44</SelectItem>
                    <SelectItem value="+33">🇫🇷 +33</SelectItem>
                    <SelectItem value="+49">🇩🇪 +49</SelectItem>
                    <SelectItem value="+91">🇮🇳 +91</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Input
                  id="phone"
                  placeholder="Enter phone number"
                  value={formData.phoneNumber}
                  onChange={(e) => {
                    // Only allow digits and limit to 10
                    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData(prev => ({ ...prev, phoneNumber: digitsOnly }));
                  }}
                  className="text-xs"
                  required
                />
              </div>
            </div>
          </div>

          {/* Message */}
          <div className="space-y-1">
            <Label className="text-xs font-medium">Message</Label>
            <Textarea
              placeholder="Edit your message here..."
              value={formData.message || getMessagePreview()}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              className="text-xs min-h-[80px]"
              autoCorrect="on"
              spellCheck="true"
            />
          </div>

          {/* Email (optional) */}
          <div className="flex items-center justify-between">
            <Label htmlFor="share-email" className="text-xs font-medium">
              Email (Optional)
            </Label>
            <Switch
              id="share-email"
              checked={formData.shareOnEmail}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, shareOnEmail: checked }))}
            />
          </div>

          {/* Email field - shown when shareOnEmail is true */}
          {formData.shareOnEmail && (
            <div className="space-y-1">
              <Label htmlFor="email" className="text-xs font-medium">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="text-xs"
                required={formData.shareOnEmail}
              />
            </div>
          )}

          {/* Assign to User (Admin only) */}
          {userRole === 'admin' && (
            <div className="space-y-1">
              <Label className="text-xs font-medium">Assign to User</Label>
              <Select
                value={formData.assignTo || undefined}
                onValueChange={(value) => setFormData(prev => ({ ...prev, assignTo: value }))}
                disabled={loadingTeam || !teamMembers.length}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingTeam ? "Loading team members..." : teamMembers.length ? "Select a user" : "No users available"} />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers.length ? (
                    teamMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="unavailable" disabled>
                      No users available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button type="submit" variant="accent" className="px-8" disabled={isLoading}>
              {isLoading ? "Sending..." : "Send"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
